<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import StarsAnimation from "@/components/StarsAnimation.vue";
import PictureMarker from "./PictureMarker.vue";
import ObserverWrapper from "../../ObserverWrapper.vue";
import GenerateButtons from "./GenerateButtons.vue";
import ChangeResult from "./ChangeResult.vue";
import MakePreview from "./MakePreview.vue";
import SlidesPreview from "./SlidesPreview.vue";
import VersionBar from "./VersionBar.vue";
import ShapePicker from "@/components/inputs/ShapePicker.vue";
import BlankFiller from "./BlankFiller.vue";
import MakeProgress from "./MakeProgress.vue";
import PictureRefusal from "./PictureRefusal.vue";
import { stripInline } from "@/utils/markdown";
import { listBlanks } from "@/utils/blanks";

const store = useStore();
defineEmits(["showGenerateButtons"]);

const generatedText = computed(() => store.state.result.generatedText);
const finishedText = computed(() => store.state.result.finishedText);
const loadingFix = computed(() => store.state.result.loadingFix);
const editMode = computed(() => store.state.result.editMode);
const loading = computed(() => store.state.result.loading);
const error = computed(() => store.state.result.error);
const vote = computed(() => store.state.result.vote);
const prompt = computed(() => store.state.prompt);
const output = computed(() => store.state.result.output);
const image = computed(() => store.state.result.image);
const imageLoading = computed(() => store.state.result.imageLoading);
const imageError = computed(() => store.state.result.imageError);
const getImage = () => store.dispatch("result/getImage");
const imageChange = computed(() => store.state.result.imageChange);
// A failed edit tries the same change again rather than drawing afresh
const retryImage = () =>
  store.dispatch("result/getImage", { change: imageChange.value });
const sources = computed(() => store.state.result.sources);
const refusal = computed(() => store.state.result.refusal);

// Details the AI didn't know, left as {{blanks}} for the user to fill in
const blankValues = ref({});
const blanks = computed(() =>
  loading.value || editMode.value || output.value === "image"
    ? []
    : listBlanks(generatedText.value)
);
watch(blanks, (current) => {
  blankValues.value = Object.fromEntries(
    Object.entries(blankValues.value).filter(([blank]) =>
      current.includes(blank)
    )
  );
});
// Tapping a blank, or the blanks button in the header, opens the fill-in bar there;
// closing it puts what was typed into the make
const fillingBlank = computed(() => store.state.result.fillingBlank);
const showBlank = (blank) => store.dispatch("result/startFillingBlanks", blank);
const fillInBlanks = () => {
  store.commit("result/SET_FILLING_BLANK", "");
  store.dispatch("result/fillBlanks", blankValues.value);
  blankValues.value = {};
};

// Widened on a computer, the make reads at a comfortable width
const isMobile = computed(() => store.state.isMobile);
const expanded = computed(() => store.state.result.expanded);

// Edit mode works sentence by sentence, so show each without its Markdown
const displaySentence = (text) => {
  if (/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(text)) return "";
  return stripInline(
    text.replace(/^#{1,6}\s+/, "").replace(/^\s*[-*+]\s+/, "• ")
  );
};

const setVote = (vote) => store.dispatch("result/setVote", vote);
const deleteSentence = (indexArray) =>
  store.dispatch("result/deleteSentence", indexArray);
const getChange = ({
  originalText,
  textToChange,
  commentToChange,
  selectedAll,
}) =>
  store.dispatch("result/getChange", {
    originalText,
    textToChange,
    commentToChange,
    selectedAll,
  });

const textContainer = ref();
const clickedIndexes = ref([]);
const previousClickedIndexes = ref([]);

const toggleClicked = (index) => {
  // One change at a time: the text can't be picked again until it's made
  if (loadingFix.value) return;
  if (clickedIndexes.value.includes(index)) {
    clickedIndexes.value = clickedIndexes.value.filter((i) => i !== index);
  } else {
    clickedIndexes.value = [...clickedIndexes.value, index];
  }
};

// Redraw part: circle parts of the picture and say what should go there
const marking = ref(false);
const startMarking = async () => {
  if (editMode.value) await store.dispatch("result/toggleEditMode");
  marking.value = true;
};
const redrawPart = (marked) => {
  marking.value = false;
  store.dispatch("result/redrawPart", marked);
};
watch(image, () => (marking.value = false));

// Changing a picture uses Edit: the whole description is picked, ready for the
// change to be typed in, and the picture is then edited rather than redrawn
const changePicture = async () => {
  if (!editMode.value) {
    await store.dispatch("result/toggleEditMode");
  }
  previousClickedIndexes.value = [];
  clickedIndexes.value = finishedText.value
    .map((sentence, index) => (sentence.highlightable ? index : -1))
    .filter((index) => index >= 0);
  await nextTick();
  const input = textContainer.value?.querySelector("input, textarea");
  input?.scrollIntoView({ block: "center", behavior: "smooth" });
  input?.focus({ preventScroll: true });
};

const toggleSelectAll = () => {
  if (previousClickedIndexes.value.length === 0) {
    previousClickedIndexes.value = clickedIndexes.value;
    clickedIndexes.value = [];
    finishedText.value.forEach((sentence, index) => {
      if (sentence.highlightable) {
        clickedIndexes.value.push(index);
      }
    });
  } else if (
    clickedIndexes.value.length === previousClickedIndexes.value.length
  ) {
    clickedIndexes.value = [];
  } else {
    clickedIndexes.value = previousClickedIndexes.value;
    previousClickedIndexes.value = [];
  }
};

const deleteSelected = () => {
  deleteSentence(clickedIndexes.value);
  clickedIndexes.value = [];
  previousClickedIndexes.value = [];
  store.commit("result/SET_COMMENT_TO_CHANGE", "");
};

const findLargestIndex = (arr) => {
  let largest = 0;
  arr.forEach((index) => {
    if (index > largest) {
      largest = index;
    }
  });
  return largest;
};

const mergeSelections = () => {
  let finalSentence = "";
  let merged = [];
  let justMerged = false;
  let toMerge = "";
  let possibleMerge = "";
  finishedText.value.forEach((sentence, index) => {
    if (clickedIndexes.value.includes(index)) {
      if (!justMerged) {
        finalSentence += "[[" + sentence.text;
        justMerged = true;
      } else {
        finalSentence += possibleMerge + sentence.text;
      }
      toMerge += possibleMerge + sentence.text;
      possibleMerge = "";
    } else if (!justMerged) {
      finalSentence += sentence.text;
    } else if (sentence.text === " ") {
      possibleMerge += " ";
    } else {
      merged.push(toMerge);
      finalSentence += "]]" + possibleMerge + sentence.text;
      possibleMerge = "";
      toMerge = "";
      justMerged = false;
    }
  });
  if (justMerged) {
    merged.push(toMerge);
    finalSentence += "]]";
  }
  return { originalText: finalSentence, textToChange: merged };
};

const submit = (commentToChange) => {
  if (highlightableCount.value === clickedIndexes.value.length) {
    getChange({
      originalText: generatedText.value,
      textToChange: [],
      commentToChange,
      selectedAll: true,
    });
    return;
  } else {
    const { originalText, textToChange } = mergeSelections();
    getChange({
      originalText,
      textToChange,
      commentToChange,
      selectedAll: false,
    });
  }
};

watch(loadingFix, (newLoading) => {
  if (!newLoading) {
    clickedIndexes.value = [];
    previousClickedIndexes.value = [];
    store.commit("result/SET_COMMENT_TO_CHANGE", "");
  }
});

// After a change, an Undo sits at the end of the changed text and goes back to the
// version before it
const versionIndex = computed(() => store.state.result.versionIndex);
const lastChanged = computed(() =>
  finishedText.value.reduce(
    (last, sentence, index) =>
      sentence.changed && sentence.highlightable ? index : last,
    -1
  )
);
const canUndoChange = computed(
  () =>
    editMode.value &&
    !loadingFix.value &&
    !imageLoading.value &&
    versionIndex.value > 0 &&
    lastChanged.value >= 0 &&
    clickedIndexes.value.length === 0
);
const undoChange = () =>
  store.dispatch("result/selectVersion", versionIndex.value - 1);

// a computed property that returns the largest index in the clickedIndexes array
const largestIndex = computed(() => findLargestIndex(clickedIndexes.value));
// computed property that returns the number of items in finishedText that are highlightable
const highlightableCount = computed(
  () => finishedText.value.filter((sentence) => sentence.highlightable).length
);
</script>

<template>
  <section
    id="shepherd-target-make"
    :class="{ 'max-w-3xl': expanded && !isMobile }"
  >
    <transition name="fade" mode="out-in" appear :duration="500">
      <ObserverWrapper @hidden="$emit('showGenerateButtons', $event)">
        <GenerateButtons />
      </ObserverWrapper>
    </transition>

    <VersionBar v-if="!loading" />

    <div v-if="output === 'image' && !loading" class="mt-6">
      <PictureMarker
        v-if="image && marking"
        :src="image"
        :alt="prompt"
        @submit="redrawPart"
        @cancel="marking = false"
      />
      <div v-else-if="image" class="relative w-full max-w-xl">
        <img
          :src="image"
          :alt="prompt"
          class="w-full rounded-lg shadow transition-opacity"
          :class="{ 'opacity-40': imageLoading }"
        />
        <p
          v-if="imageLoading"
          class="absolute inset-0 flex items-center justify-center text-sm font-bold text-primary"
        >
          {{
            imageChange
              ? "Changing the picture..."
              : "Drawing another version..."
          }}
        </p>
      </div>
      <div
        v-else-if="imageLoading"
        class="relative flex h-64 w-full max-w-xl items-center justify-center rounded-lg bg-gray-10"
      >
        <StarsAnimation class="absolute inset-0 m-auto" :width="300" />
        <p class="relative text-sm font-bold text-secondary">
          Drawing your make...
        </p>
      </div>
      <div
        v-else-if="imageError"
        class="flex h-32 w-full max-w-xl flex-col items-center justify-center rounded-lg bg-gray-10"
      >
        <p class="text-sm font-medium text-red-500">{{ imageError }}</p>
        <button
          type="button"
          class="mt-2 text-sm font-bold uppercase text-accentPrimary"
          @click="retryImage"
        >
          Try again
        </button>
      </div>
      <div
        v-else-if="generatedText && !refusal"
        class="flex h-32 w-full max-w-xl flex-col items-center justify-center rounded-lg bg-gray-10"
      >
        <p class="text-sm text-secondary">
          This version's picture is no longer kept
        </p>
        <button
          type="button"
          class="mt-2 text-sm font-bold uppercase text-accentPrimary"
          @click="getImage"
        >
          Draw it again
        </button>
      </div>
      <div
        v-if="(image || imageError) && !marking"
        class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2"
      >
        <button
          type="button"
          class="flex items-center text-sm font-bold uppercase text-accentPrimary hover:text-purple-400 disabled:opacity-50"
          :disabled="imageLoading"
          @click="getImage"
        >
          <font-awesome-icon :icon="['fas', 'rotate-right']" class="mr-2" />
          Another version
        </button>
        <button
          v-if="image"
          type="button"
          class="flex items-center text-sm font-bold uppercase text-accentPrimary hover:text-purple-400 disabled:opacity-50"
          :disabled="imageLoading || loadingFix"
          @click="changePicture"
        >
          <font-awesome-icon :icon="['fas', 'pencil']" class="mr-2" />
          Change picture
        </button>
        <button
          v-if="image"
          type="button"
          class="flex items-center text-sm font-bold uppercase text-accentPrimary hover:text-purple-400 disabled:opacity-50"
          :disabled="imageLoading || loadingFix"
          title="Circle parts of the picture and say what should go there"
          @click="startMarking"
        >
          <font-awesome-icon :icon="['fas', 'paintbrush']" class="mr-2" />
          Redraw part
        </button>
        <ShapePicker @picked="getImage" />
      </div>
      <p
        v-if="image && imageError"
        class="mt-2 text-sm font-medium text-red-500"
        role="alert"
      >
        {{ imageError }}
        <button
          v-if="imageChange"
          type="button"
          class="ml-2 font-bold uppercase text-accentPrimary"
          @click="retryImage"
        >
          Try again
        </button>
      </p>
      <p v-if="image && editMode" class="mt-2 text-xs text-secondary">
        Pick the parts of the description to change and say what you'd like
        different. The picture is changed to match, keeping the rest of it.
      </p>
      <PictureRefusal v-if="refusal" />
      <p class="mt-4 text-xs font-bold uppercase text-secondary">Description</p>
    </div>

    <div class="relative sm:mb-12 mb-28 mt-4 tabs" ref="textContainer">
      <MakeProgress v-if="loading && !error" />
      <p v-if="finishedText && editMode" class="[overflow-wrap:anywhere]">
        <span v-for="(sentence, index) in finishedText" :key="index">
          <span
            v-if="sentence.highlightable"
            class="rounded-lg p-1 -m-1 transition-all duration-200 whitespace-pre-line cursor-pointer"
            :class="
              (!clickedIndexes.includes(index) &&
                sentence.changed &&
                loadingFix) ||
              (!clickedIndexes.includes(index) &&
                sentence.changed &&
                !loadingFix)
                ? 'bg-accentPrimary/[0.12] hover:bg-accentPrimary/25'
                : clickedIndexes.includes(index)
                ? 'bg-yellow-300 hover:bg-opacity-70 bg-opacity-90'
                : 'hover:bg-yellow-300 hover:bg-opacity-90'
            "
            @click="toggleClicked(index)"
          >
            <span :class="{ 'font-bold': /^#{1,6}\s/.test(sentence.text) }">{{
              displaySentence(sentence.text)
            }}</span>
          </span>
          <button
            v-if="canUndoChange && index === lastChanged"
            type="button"
            class="ml-2 inline-flex -translate-y-px items-center rounded-full border border-gray-200 bg-white px-2.5 py-0.5 align-middle text-xs font-bold text-accentPrimary shadow-sm transition-colors hover:border-accentPrimary"
            title="Undo this change"
            @click.stop="undoChange"
          >
            <font-awesome-icon :icon="['fas', 'rotate-left']" class="mr-1" />
            Undo
          </button>
          <span v-else-if="sentence.text === ' '">{{ " " }}</span>
          <br v-else-if="sentence.text === '\n'" />
          <section v-if="clickedIndexes.length > 0 && index === largestIndex">
            <ChangeResult
              @submit="submit($event)"
              @selectAll="toggleSelectAll"
              @deleteSelected="deleteSelected"
            />
          </section>
        </span>
      </p>
      <PictureRefusal v-else-if="refusal && !generatedText" />
      <p
        v-else-if="error"
        class="text-base text-red-500 underline text-center font-bold whitespace-pre-line"
      >
        {{ error }}
      </p>
      <SlidesPreview
        v-else-if="output === 'pptx'"
        :text="generatedText"
        :fillable="blanks.length > 0"
        :blankValues="blankValues"
        @blank="showBlank"
      />
      <MakePreview
        v-else
        :text="generatedText"
        :plain="output === 'text' || output === 'image'"
        :fillable="blanks.length > 0"
        :blankValues="blankValues"
        @blank="showBlank"
      />
      <section
        v-if="!editMode && sources.length && !loading"
        class="mt-6 rounded-lg bg-gray-10 p-4"
      >
        <p class="mb-2 text-xs font-bold uppercase text-secondary">Sources</p>
        <ol class="list-decimal space-y-1 pl-5 text-sm">
          <li v-for="source in sources" :key="source.url">
            <a
              :href="source.url"
              target="_blank"
              rel="noopener noreferrer nofollow"
              class="text-accentPrimary underline hover:text-purple-400 [overflow-wrap:anywhere]"
              >{{ source.title }}</a
            >
          </li>
        </ol>
      </section>
      <StarsAnimation v-if="loading" class="-mt-44 -z-50" :width="400" />
      <div
        class="flex flex-col mt-2 lg:mb-[12rem] w-full"
        v-else-if="!error && !(refusal && !generatedText)"
      >
        <div class="flex justify-between mt-6">
          <p class="text-base font-bold text-primary">
            Was this generation good or bad?
          </p>
          <div class="flex items-center">
            <div
              class="grid grid-cols-1 mr-5 p-3 -m-3 cursor-pointer"
              :class="
                vote === 1
                  ? 'text-accentPrimary hover:text-purple-400'
                  : 'text-secondary hover:text-primary'
              "
              @click="setVote(1)"
            >
              <font-awesome-icon
                class="mx-auto text-xl"
                :icon="['fas', 'thumbs-up']"
              />
              <p class="text-xs text-center font-bold mt-0.5 whitespace-nowrap">
                Good
              </p>
            </div>
            <div
              class="grid grid-cols-1 p-3 -m-3 cursor-pointer"
              :class="
                vote === -1
                  ? 'text-accentSecondary hover:text-yellow-300'
                  : 'text-secondary hover:text-primary'
              "
              @click="setVote(-1)"
            >
              <font-awesome-icon
                class="mx-auto text-xl"
                :icon="['fas', 'thumbs-down']"
              />
              <p class="text-xs text-center font-bold mt-0.5 whitespace-nowrap">
                Bad
              </p>
            </div>
          </div>
        </div>
        <p v-if="vote === -1" class="mx-5 my-1 text-secondary italic text-sm">
          If you didn't like the result try re-generate your make or go back
          with different answers, more context or style choices
        </p>
        <p
          v-else-if="vote === 1"
          class="mx-5 my-1 text-secondary italic text-sm"
        >
          If you loved your make, use the buttons at the top to copy or share
          your make with others. You can also use it as the context for a new
          make, with the two arrow button.
        </p>
      </div>
    </div>
    <!-- Room to scroll the last blanks up above the bar pinned to a phone's screen -->
    <div
      v-if="isMobile && fillingBlank && blanks.length && !error"
      class="h-[45vh]"
      aria-hidden="true"
    />
    <BlankFiller
      v-if="fillingBlank && blanks.length && !error"
      v-model="blankValues"
      :blanks="blanks"
      @done="fillInBlanks"
    />
  </section>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
